/**
 * Summaries behind the "money earned / total hours / avg hourly rate" cards.
 *
 * The Invoices tab and the Virtual Ledger both show these for a date range, and
 * they have to agree to the cent, so both read them from here rather than each
 * doing their own arithmetic.
 *
 * Hours and money reach the client as integers — hours in hundredths, money in
 * ten-thousandths — and the codebase divides at the point of display. These
 * helpers follow that: `summarizeEarnings` returns display units because that
 * is what the cards render, `summarizeLedger` returns stored units because that
 * is what its callers already divide.
 */

import moment, { Moment } from "moment";

/**
 * A date range as the pickers hand it over. Either end may be unset, and an
 * unset end means "no bound" — so an empty range covers everything.
 */
export type DateRangeTuple = [Moment | null, Moment | null];

/**
 * Whether a timestamp falls inside a range, counting whole days at both ends.
 *
 * @param {string} date Timestamp to test.
 * @param {Array} range Range to test against.
 * @returns {boolean} True if the date is in range, or the range is open.
 */
export function withinDateRange(
  date: string,
  [from, to]: DateRangeTuple
): boolean {
  const day = moment(date);
  if (from && day.isBefore(from, "day")) return false;
  if (to && day.isAfter(to, "day")) return false;
  return true;
}

/** The fields of a job the earnings summary reads, in stored units. */
export type EarningsJob = {
  hours: number;
  total: number;
  createdAt: string;
};

/** Earnings for a set of jobs, in display units — dollars and hours. */
export type EarningsSummary = {
  total: number;
  totalHours: number;
  avgHourlyRate: number;
};

/**
 * Build a summary from amounts already totalled, in stored units.
 *
 * The rate divides hourly earnings by hours rather than all earnings by hours.
 * Flat fees and stipends carry no hours, so charging them against the hours
 * somebody happened to log inflates the rate badly: on the ledger it turned a
 * workforce paid $30–$95 an hour into a headline figure of $356.
 *
 * The consequence is that `total / totalHours` no longer equals
 * `avgHourlyRate`. That is the point — they answer different questions.
 *
 * @param {number} hours Hours worked, in hundredths.
 * @param {number} total Everything earned, in ten-thousandths.
 * @param {number} hourlyTotal Earned on jobs that logged hours, same units.
 * @returns {object} Dollars earned, hours worked, and the hourly rate.
 */
export function earningsFromTotals(
  hours: number,
  total: number,
  hourlyTotal: number
): EarningsSummary {
  const totalHours = hours / 100;

  return {
    total: total / 10000,
    totalHours,
    // No hours logged isn't a divide-by-zero to paper over, it's simply no rate
    // to report. Returning NaN here reaches the card as "$NaN".
    avgHourlyRate: totalHours ? hourlyTotal / 10000 / totalHours : 0,
  };
}

/**
 * What a talent earned across a set of jobs.
 *
 * @param {Array} jobs Jobs to total up.
 * @returns {object} Dollars earned, hours worked, and the hourly rate.
 */
export function summarizeEarnings(jobs: EarningsJob[]): EarningsSummary {
  let hours = 0;
  let total = 0;
  let hourlyTotal = 0;

  jobs.forEach((job) => {
    const jobHours = job.hours ?? 0;
    hours += jobHours;
    total += job.total ?? 0;
    if (jobHours > 0) hourlyTotal += job.total ?? 0;
  });

  return earningsFromTotals(hours, total, hourlyTotal);
}

/** The fields of a virtual transaction the ledger summary reads. */
export type LedgerEntry = {
  amount: number;
  createdAt: string;
};

/** Ledger totals, in stored units — callers divide by 10000 to display. */
export type LedgerSummary = {
  credited: number;
  debited: number;
  balance: number;
};

/**
 * Credits, debits and the balance between them. Transactions carry a signed
 * amount: what a talent earned on a project lands as a credit, what has been
 * paid out lands as a debit, so the balance is what is still outstanding —
 * positive when they are owed, negative when they have been paid too much.
 *
 * @param {Array} entries Transactions to total up.
 * @returns {object} Credited, debited and balance, in stored units.
 */
export function summarizeLedger(entries: LedgerEntry[]): LedgerSummary {
  const totals = entries.reduce(
    (acc, entry) => {
      if (entry.amount > 0) acc.credited += entry.amount;
      else acc.debited += entry.amount;
      return acc;
    },
    { credited: 0, debited: 0 }
  );

  return { ...totals, balance: totals.credited + totals.debited };
}
